// Supabase Request Queue with Retry Logic
// Handles HTTP/2 protocol errors and connection issues

class SupabaseRequestQueue {
  constructor() {
    this.queue = [];
    this.processing = false;
    this.maxRetries = 3;
    this.baseDelay = 1000; // 1 second
    this.maxConcurrent = 2; // Limit concurrent requests
    this.activeRequests = 0;
    this.circuitBreakerFailures = 0;
    this.circuitBreakerThreshold = 5;
    this.circuitBreakerTimeout = 30000; // 30 seconds
    this.circuitBreakerOpenUntil = 0;
  }

  // Check if circuit breaker is open
  isCircuitBreakerOpen() {
    if (this.circuitBreakerOpenUntil > Date.now()) {
      return true;
    }
    if (this.circuitBreakerOpenUntil > 0) {
      // Reset circuit breaker
      console.log('🔄 Circuit breaker reset - attempting requests again');
      this.circuitBreakerFailures = 0;
      this.circuitBreakerOpenUntil = 0;
    }
    return false;
  }

  // Add request to queue
  async enqueue(requestFn, options = {}) {
    return new Promise((resolve, reject) => {
      const request = {
        id: Date.now() + Math.random(),
        requestFn,
        resolve,
        reject,
        retries: 0,
        priority: options.priority || 0,
        timeout: options.timeout || 10000,
        fallback: options.fallback || null,
        description: options.description || 'Supabase request'
      };

      // Insert by priority (higher priority first)
      const insertIndex = this.queue.findIndex(q => q.priority < request.priority);
      if (insertIndex === -1) {
        this.queue.push(request);
      } else {
        this.queue.splice(insertIndex, 0, request);
      }

      console.log(`📝 Queued: ${request.description} (Queue: ${this.queue.length}, Active: ${this.activeRequests})`);
      this.processQueue();
    });
  }

  // Process the queue
  async processQueue() {
    if (this.processing || this.queue.length === 0) {
      return;
    }

    this.processing = true;

    while (this.queue.length > 0 && this.activeRequests < this.maxConcurrent) {
      if (this.isCircuitBreakerOpen()) {
        console.log('⚡ Circuit breaker open - pausing requests');
        break;
      }

      const request = this.queue.shift();
      this.activeRequests++;
      
      // Process request without waiting (concurrent)
      this.processRequest(request).finally(() => {
        this.activeRequests--;
        // Continue processing queue
        setTimeout(() => this.processQueue(), 100);
      });
    }

    this.processing = false;
  }

  // Process individual request with retry logic
  async processRequest(request) {
    const startTime = Date.now();
    console.log(`🔄 Processing: ${request.description} (Attempt ${request.retries + 1})`);

    try {
      // Set timeout for the request
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), request.timeout);
      });

      const result = await Promise.race([
        request.requestFn(),
        timeoutPromise
      ]);

      // Success - reset circuit breaker failures
      this.circuitBreakerFailures = Math.max(0, this.circuitBreakerFailures - 1);
      
      const duration = Date.now() - startTime;
      console.log(`✅ Success: ${request.description} (${duration}ms)`, 'Response:', result);
      request.resolve(result);

    } catch (error) {
      const duration = Date.now() - startTime;
      console.log(`❌ Failed: ${request.description} (${duration}ms) - ${error.message}`);

      // Check if this is a retryable error
      const isRetryable = this.isRetryableError(error);
      
      if (isRetryable && request.retries < this.maxRetries) {
        // Retry with exponential backoff
        request.retries++;
        const delay = this.baseDelay * Math.pow(2, request.retries - 1) + Math.random() * 1000;
        
        console.log(`🔄 Retrying: ${request.description} in ${Math.round(delay)}ms (${request.retries}/${this.maxRetries})`);
        
        setTimeout(() => {
          // Re-add to front of queue for retry
          this.queue.unshift(request);
          this.processQueue();
        }, delay);

      } else {
        // Max retries reached or non-retryable error
        this.circuitBreakerFailures++;
        
        if (this.circuitBreakerFailures >= this.circuitBreakerThreshold) {
          console.log(`⚡ Circuit breaker opened - too many failures (${this.circuitBreakerFailures})`);
          this.circuitBreakerOpenUntil = Date.now() + this.circuitBreakerTimeout;
        }

        // Try fallback if available
        if (request.fallback) {
          console.log(`🔄 Using fallback for: ${request.description}`);
          try {
            const fallbackResult = await request.fallback();
            request.resolve(fallbackResult);
            return;
          } catch (fallbackError) {
            console.log(`❌ Fallback failed: ${fallbackError.message}`);
          }
        }

        request.reject(error);
      }
    }
  }

  // Check if error is retryable
  isRetryableError(error) {
    const message = error.message.toLowerCase();
    
    // Don't retry these "expected" errors
    const nonRetryableErrors = [
      'http 409', // Conflict - usually means duplicate key, which is expected
      'http 400', // Bad request - usually a data validation issue
      'http 404', // Not found - usually means resource doesn't exist
      'duplicate key value violates unique constraint' // Postgres duplicate key
    ];
    
    if (nonRetryableErrors.some(nonRetryable => message.includes(nonRetryable))) {
      return false;
    }
    
    const retryableErrors = [
      'failed to fetch',
      'network error',
      'connection closed',
      'connection reset',
      'http2_protocol_error',
      'timeout',
      'request timeout',
      // Neither of these was listed anywhere, so they fell through as
      // non-retryable and went straight to the fallback. But the bearer token
      // is resolved fresh on every attempt, and the usual cause of a 401 here
      // is the session being momentarily unreadable during a token refresh -
      // a wait of a second or two is exactly what heals it. A genuine denial
      // still fails, just after three tries instead of none.
      'http 401',
      'http 403',
      'http 500', // Server error
      'http 502', // Bad gateway
      'http 503', // Service unavailable
      'http 504'  // Gateway timeout
    ];

    return retryableErrors.some(retryableError => 
      message.includes(retryableError)
    );
  }

  // Get queue status
  getStatus() {
    return {
      queueLength: this.queue.length,
      activeRequests: this.activeRequests,
      circuitBreakerOpen: this.isCircuitBreakerOpen(),
      circuitBreakerFailures: this.circuitBreakerFailures
    };
  }

  // Clear queue (emergency)
  clearQueue() {
    console.log('🧹 Clearing request queue');
    this.queue.forEach(request => {
      request.reject(new Error('Queue cleared'));
    });
    this.queue = [];
  }
}

// Global queue instance
const supabaseQueue = new SupabaseRequestQueue();

// Helper function to wrap Supabase calls
export const queueSupabaseRequest = async (requestFn, options = {}) => {
  return supabaseQueue.enqueue(requestFn, options);
};

// Helper function to get queue status
export const getQueueStatus = () => {
  return supabaseQueue.getStatus();
};

// Helper function to clear queue
export const clearQueue = () => {
  supabaseQueue.clearQueue();
};

// Export the queue instance for advanced usage
export { supabaseQueue };

export default supabaseQueue;
